<i18n>
{
  "cl": {
    "title": "Evaluaciones automáticas",
    "tooltip": "Evaluaciones automáticas",
    "tabTitleInitial": "Faltan requisitos",
    "tabTitleCommercial": "Gestión comercial",
    "TabTitleRisk": "Revisión por Riesgo",
    "emptyEvaluationsSearchMessage": "No se encontraron evaluaciones",
    "tabTooltipInitial": "Simulaciones aún no tienen toda la información necesaria (o no cumplen con la política) para poder evaluarlas",
    "tabTooltipCommercial": "Se obtuvo la info para la simulación, pero el resultado fue menor al esperado o se necesita la info del codeudor antes de enviar a riesgo.",
    "tabTooltipRisk": "Evaluación automática exitosa que debe ser o bien ratificada o bien recaulculada por el equipo de riesgo, en caso de contar con nuevos antecedentes",
    "reviewable": "Revisable"
  }
}
</i18n>
<template>
  <div id="evaluations-panel" class="mt-6 pb-5">
    <div class="box evaluations-filter">
      <div class="columns">
        <div class="column is-4">
          <b-autocomplete
            :data="filteredEvaluations"
            :loading="isLoading"
            class="search-bar"
            field="name"
            icon="search"
            placeholder="Buscar por RUT, nombre o correo"
            rounded
            size="is-normal"
            @select="option => selected = option"
            @typing="getAsyncData">
            <template slot-scope="props">
              <div>
                <span>{{ props.option.name }}</span><br>
                <span>{{ props.option.idNumber }}</span>
              </div>
            </template>
            <template #empty>{{ $t('emptyEvaluationsSearchMessage') }}</template>
          </b-autocomplete>
        </div>
        <div class="column is-8 options">
          <div id="executive-filter-container">
            <b-select
              id="executive-filter"
              v-model="executiveSelected"
              name="month"
              placeholder="Seleccione ejecutivo"
              rounded
              size="is-normal"
              @input="onChangeExecutive($event)">
              <option selected value="-1">Todos</option>
              <option
                v-for="executive in executives"
                :key="executive.email"
                :value="executive.email">
                {{ executive.name }}
              </option>
            </b-select>
          </div>
          <dashboard-filter
            :cache="true"
            @dashboard-range-date-change="onChangeRangeDate" />
        </div>
      </div>
    </div>
    <div class="box evaluations-content">
      <div v-if="!isLoadingEvaluations" class="columns">
        <div v-for="(tab, stage) in tabs" :key="stage" class="column is-4">
          <EvaluationColumn
            :id="`${stage}-evaluation`"
            :data="tab.data"
            :title="$t(tab.title)"
            :tooltip="$t(tab.tooltip)"
            :type="stage"
            :loading="tab.loading"
            @more-view="viewMore(stage)" />
        </div>
      </div>
      <Spinner v-if="isLoadingEvaluations" />
    </div>
  </div>
</template>

<script>
import { debounce } from '@/infrastructure/lodash';
import DashboardFilter from '@/ui/components/internal/dashboard/DashboardFilter.vue';
import EvaluationColumn from '@/ui/components/internal/evaluations/EvaluationColumn.vue';
import Spinner from '@/ui/components/Spinner.vue';
import { RealEstatesClient } from '@/application/services/api-clients/real-estates-client';
import { EvaluationsClient } from '@/application/services/evaluations/apis/evaluations-client';
import { evaluationsBackend } from '@/application/api';
import { authenticationService } from '@/application/services/api-clients/login-service';
import '@/ui/assets/css/custom-components.css';
import { getSafe } from '@/application/services/get-safe';

export default {
  name: 'Evaluations',
  components: {
    Spinner,
    EvaluationColumn,
    DashboardFilter
  },
  data() {
    return {
      dates: [],
      executiveSelected: null,
      tabs: {
        initial: {
          title: 'tabTitleInitial',
          tooltip: 'tabTooltipInitial',
          data: { data: [], total: 0 },
          currentPage: 1
        },
        commercial: {
          title: 'tabTitleCommercial',
          tooltip: 'tabTooltipCommercial',
          data: { data: [], total: 0 },
          currentPage: 1
        },
        risk: {
          title: 'TabTitleRisk',
          tooltip: 'tabTooltipRisk',
          data: { data: [], total: 0 },
          currentPage: 1
        }
      },
      executives: [],
      filteredEvaluations: [],
      selected: null,
      evaluationClient: new EvaluationsClient(evaluationsBackend, authenticationService),
      isLoading: false,
      isLoadingEvaluations: true
    };
  },
  watch: {
    selected(newValue) {
      if (newValue) {
        this.$router.push({
          name: 'evaluation-detail',
          params: { evaluationId: newValue.id }
        });
      }
    }
  },
  async mounted() {
    await this.getExecutives();
    this.setDefaultExecutive();
  },
  methods: {
    async getExecutives() {
      this.executives = await RealEstatesClient.getExecutives();
    },
    async setDefaultExecutive() {
      if (this.$store.state.evaluationExecutiveFilter) {
        this.executiveSelected = this.$store.state.evaluationExecutiveFilter;
        await this.loadEvaluations();
        return;
      }

      const currentUserEmail = this.$store.state.currentUser.user.email;
      const executiveExists = this.executives.some(exec => exec.email === currentUserEmail);

      if (executiveExists) {
        this.executiveSelected = currentUserEmail;
      } else {
        this.executiveSelected = '-1';
      }
    },
    // eslint-disable-next-line func-names
    getAsyncData: debounce(async function (searchString) {
      try {
        this.isLoading = true;
        this.filteredEvaluations = await this.evaluationClient.getEvaluationsBySearchString(searchString);
      } finally {
        this.isLoading = false;
      }
    }, 500),
    async onChangeExecutive(event) {
      this.$store.state.evaluationExecutiveFilter = event;
      this.executiveSelected = event;
      await this.loadEvaluations();
    },
    async onChangeRangeDate(date) {
      this.$store.state.evaluationDateFilter = date;
      this.dates = date;
      await this.loadEvaluations();
    },
    async viewMore(stage) {
      const tab = this.tabs[stage];
      tab.currentPage = tab.data.data.length / 10 + 1;
      await this.loadEvaluationsForTab(stage, true);
    },
    async fetchEvaluationsForStage(startDate, endDate, executiveEmail, stage, currentTab = 1, tabLoading = false) {
      this.isLoadingEvaluations = !tabLoading;
      const tab = this.tabs[stage];
      try {
        tab.loading = tabLoading;
        return await this.evaluationClient.getEvaluationsByDateAndExecutive(startDate, endDate, executiveEmail, stage, currentTab);
      } catch (_error) {
        return { data: [], total: 0 };
      } finally {
        this.isLoadingEvaluations = false;
        tab.loading = false;
      }
    },
    async loadEvaluationsForTab(stage, tabLoading = false) {
      const tab = this.tabs[stage];
      const data = await this.fetchEvaluationsForStage(
        this.dates[0],
        this.dates[1],
        this.executiveSelected,
        stage,
        tab.currentPage,
        tabLoading
      );
      if (tab.currentPage === 1) {
        tab.data.data = [];
      }
      tab.data.data.push(...data.data);
      tab.data.total = getSafe(data, 'total', 0);
    },
    async loadEvaluations() {
      const stages = Object
        .keys(this.tabs)
        // eslint-disable-next-line no-prototype-builtins
        .filter(stage => this.tabs.hasOwnProperty(stage));

      const promises = stages.map(async stage => {
        const tab = this.tabs[stage];
        tab.data.data = [];
        tab.data.total = 0;
        tab.currentPage = 1;

        return this.loadEvaluationsForTab(stage);
      });

      try {
        await Promise.all(promises);
      } finally {
        this.isLoadingEvaluations = false;
      }
    }
  }
};
</script>
<style scoped>
h2 {
  color: var(--color-nickel);
}

.column {
  padding: 10px;
}

.box {
  border-radius: 10px;
}

#executive-filter-container {
  margin-right: 20px;
}

@media only screen and (max-width: 768px) {
  #executive-filter-container {
    margin-bottom: 1rem;
  }

  #executive-filter-container {
    width: 100% !important;
  }
}

@media only screen and (min-width: 769px) {
  .evaluations-filter .options {
    display: flex;
  }
}
</style>
<style>

#evaluations-panel .datepicker .icon {
  color: var(--primary) !important;
}
@media only screen and (max-width: 768px) {
  #executive-filter-container span.select,
  #executive-filter-container #executive-filter {
    width: 100% !important;
  }
}
@media only screen and (min-width: 769px) and (max-width: 1160px) {
  .evaluations-content .columns{
   display: block;
  }
  .evaluations-content .columns .column {
    width: 100%;
  }
}
</style>
